import { useMemo } from 'react';
import {
  can as canRole,
  isAtLeast as isAtLeastRole,
  type TCapability,
  type TRole,
} from '../auth/roles';
import { useAuthorizationContext } from '../providers/AuthorizationProvider';

export interface IUseAuthorizationReturn {
  /** The current user's role, or `null` when logged out. */
  role: TRole | null;
  /** True while auth state or the role claim is still resolving. */
  isLoading: boolean;
  /** A user is signed in. */
  isAuthenticated: boolean;
  /** Exact role match. */
  hasRole: (role: TRole) => boolean;
  /** True if the current role outranks or equals `min`. */
  isAtLeast: (min: TRole) => boolean;
  /** True if the current role holds the given capability. */
  can: (capability: TCapability) => boolean;
  /** True if the current role is any of the provided roles. */
  hasAnyRole: (roles: TRole[]) => boolean;
  /** Force-refresh the token and re-read the role claim (after self role change). */
  refreshRole: () => Promise<void>;
}

/**
 * Authorization helpers for the current user. A `null` role (logged out) never
 * satisfies any check. Pure logic is delegated to `src/auth/roles.ts`.
 */
function useAuthorization(): IUseAuthorizationReturn {
  const { role, isLoading, isAuthenticated, refreshRole } = useAuthorizationContext();

  // Memoized on `role` so the returned helpers keep stable identities between
  // renders — safe to use in consumer dependency arrays.
  return useMemo<IUseAuthorizationReturn>(
    () => ({
      role,
      isLoading,
      isAuthenticated,
      refreshRole,
      hasRole: (target: TRole): boolean => role === target,
      isAtLeast: (min: TRole): boolean => (role ? isAtLeastRole(role, min) : false),
      can: (capability: TCapability): boolean => (role ? canRole(role, capability) : false),
      hasAnyRole: (roles: TRole[]): boolean => (role ? roles.includes(role) : false),
    }),
    [role, isLoading, isAuthenticated, refreshRole]
  );
}

export default useAuthorization;
